import useTranslator from "../../hooks/useTranslator";
import {
  getCustomIncomeDetails,
  haveValue,
  toTitleCase,
} from "../../helper/common";
import Arrow from "../../assets/img/dark-arrow.svg";
import userIcon from "../../assets/img/user-icon.svg";
import { useEffect, useMemo, useRef, useState } from "react";

const CandidateSummary = ({ originalUser, allUsers, definedRent }) => {
  const { _t } = useTranslator();

  const getIncome = (_user, incomeSource) => {
    const fallbackIncome = _user?.useFallbackIncome;
    const useFallbackIncome =
      typeof fallbackIncome === "boolean" ? fallbackIncome : true;

    return getCustomIncomeDetails(
      _user,
      originalUser,
      _t,
      incomeSource,
      undefined,
      useFallbackIncome,
      false
    );
  };

  const getEffortRatePercentage = (combinedIncome) => {
    if (!haveValue(combinedIncome) || combinedIncome === 0)
      return _t("noIncome");
    else
      return `${Math.ceil(
        (+definedRent /
          (haveValue(combinedIncome) && combinedIncome != 0
            ? combinedIncome
            : definedRent)) *
          100
      )}%`;
  };

  const tenantIncomes = useMemo(() => {
    return (
      allUsers?.map((user) =>
        getIncome(user?.tenant_id, user?.income_selection?.tenant_source)
      ) || []
    );
  }, [allUsers]);

  const guarantorIncomes = useMemo(() => {
    return (
      allUsers?.flatMap((tenantUser) =>
        (tenantUser?.tenant_id?.gurantor_id || []).map((g) =>
          getIncome(
            g,
            tenantUser?.income_selection?.guarantor_sources?.[g?._id]
          )
        )
      ) || []
    );
  }, [allUsers]);

  const allTenantIncome = tenantIncomes.reduce(
    (sum, u) => sum + (+u?.amount || 0),
    0
  );
  const allGuarantorIncome = guarantorIncomes.reduce(
    (sum, u) => sum + (+u?.amount || 0),
    0
  );

  const containerRefs = useRef([]);
  const [wrapped, setWrapped] = useState([]);

  useEffect(() => {
    const viewportWidth = window.innerWidth;
    const thresholdPx = (100 / 1440) * viewportWidth;
    const newHeights = containerRefs.current.map(
      (ref) => ref?.offsetHeight || 0
    );

    const newWrapped = newHeights.map((height) => height > thresholdPx);
    setWrapped(newWrapped);
  }, [allUsers]);

  const guarantorsContainerRefs = useRef([]);
  const [guarantorsWrapped, setGuarantorsWrapped] = useState([]);

  useEffect(() => {
    const viewportWidth = window.innerWidth; // current screen width
    const thresholdPx = (100 / 1440) * viewportWidth;
    const newHeights = guarantorsContainerRefs.current.map(
      (ref) => ref?.offsetHeight || 0
    );

    const newWrapped = newHeights.map((height) => height > thresholdPx);
    setGuarantorsWrapped(newWrapped);
  }, [allUsers]);

  return (
    <>
      <div className="candidate-summary-table">
        <div className="candidate-summary-report-header">
          <div className="candidate-summary-report-title">
            <div className="candidate-summary-report-icon-person">
              <img src={userIcon} alt="" />
            </div>
            <h2>{_t("tenants")}</h2>
          </div>
          <div className="candidate-summary-report-effort-rate">
            <span className="candidate-summary-report-label">
              {_t("combinedEffortRate")}
            </span>
            <span className="candidate-summary-report-rate-box">
              {getEffortRatePercentage(allTenantIncome)}
            </span>
          </div>
        </div>

        {allUsers?.map((user, index) => (
          <div
            key={index}
            ref={(el) => (containerRefs.current[index] = el)}
            className={`candidate-summary-report-tenant-row ${
              wrapped[index] && "flex-wrap"
            }`}
          >
            <div className="candidate-summary-report-tenant-info">
              <img src={Arrow} alt="arrow" />
              <span className="candidate-summary-report-name">
                {user?.tenant_id?.onfido_user_kyc?.onfido_doc_data
                  ?.first_name ? (
                  <span>
                    {toTitleCase(
                      `${user?.tenant_id?.onfido_user_kyc?.onfido_doc_data?.first_name} ${user?.tenant_id?.onfido_user_kyc?.onfido_doc_data?.last_name}`
                    )}
                  </span>
                ) : user?.tenant_id?.forensic_name ? (
                  <span>{toTitleCase(user?.tenant_id?.forensic_name)}</span>
                ) : user?.tenant_id?.fname ? (
                  <span>{`${user?.tenant_id?.fname} ${user?.tenant_id?.lname}`}</span>
                ) : (
                  <span>N/A</span>
                )}{" "}
              </span>
              <span className="candidate-summary-report-tag">
                {_t("tenant")} {index + 1}
              </span>
            </div>
            {(() => {
              const userData = getIncome(
                user?.tenant_id,
                user?.income_selection?.tenant_source
              );
              if (!Array.isArray(userData?.source)) return null;

              return (
                <>
                  <div
                    className={`candidate-summary-report-income-details ${
                      wrapped[index] && "order-2 m-0"
                    }`}
                  >
                    <div className="candidate-summary-report-label">
                      {_t("incomeSource")}
                    </div>
                    {userData.source.map((src, idx) => {
                      if (src?.startsWith?.("payslip:")) {
                        const [label, rawDate] = src.split(":");
                        const parts = rawDate.trim().split(" ");

                        let month = parts[0].replace(",", "");
                        const hasComma = parts[0].includes(",");
                        const rest = parts.slice(1).join(" ");

                        return (
                          <span
                            key={idx}
                            className="candidate-summary-report-source-tag"
                          >
                            {_t(label)}: {_t(month)}
                            {hasComma ? "," : ""}
                            {rest ? " " + rest : ""}
                          </span>
                        );
                      }

                      return (
                        <span
                          key={idx}
                          className="candidate-summary-report-source-tag"
                        >
                          {_t(src)}
                        </span>
                      );
                    })}
                  </div>

                  <span
                    className={`candidate-summary-report-income-box ${
                      wrapped[index] && "order-1"
                    }`}
                  >
                    {_t("income")}: {userData?.amount ?? 0} €
                  </span>
                </>
              );
            })()}
          </div>
        ))}
      </div>

      {allUsers?.some(
        (tenantUser) =>
          Array.isArray(tenantUser?.tenant_id?.gurantor_id) &&
          tenantUser.tenant_id.gurantor_id.length > 0
      ) && (
        <div className="candidate-summary-table">
          <div className="candidate-summary-report-header">
            <div className="candidate-summary-report-title">
              <div className="candidate-summary-report-icon-person">
                <img src="/images/guarantor-icon.svg" alt="" />
              </div>
              <h2>{_t("guarantors")}</h2>
            </div>
            <div className="candidate-summary-report-effort-rate">
              <span className="candidate-summary-report-label">
                {_t("combinedEffortRate")}
              </span>
              <span className="candidate-summary-report-rate-box">
                {" "}
                {getEffortRatePercentage(allGuarantorIncome)}
              </span>
            </div>
          </div>

          {allUsers?.map(
            (tenantUser, index) =>
              Array.isArray(tenantUser?.tenant_id?.gurantor_id) &&
              tenantUser.tenant_id.gurantor_id.map((user, gIdx) => (
                <div
                  key={gIdx}
                  ref={(el) => (guarantorsContainerRefs.current[index] = el)}
                  className={`candidate-summary-report-tenant-row-without-border ${
                    gIdx === tenantUser.tenant_id.gurantor_id.length - 1
                      ? "candidate-summary-border-bottom"
                      : ""
                  }
                    ${guarantorsWrapped[index] && "flex-wrap"}
                `}
                >
                  <div className="candidate-summary-report-tenant-info">
                    <img src={Arrow} alt="arrow" />
                    <span className="candidate-summary-report-name">
                      {user?.onfido_user_kyc?.onfido_doc_data?.first_name ? (
                        <span>
                          {`${user?.onfido_user_kyc?.onfido_doc_data?.first_name} ${user?.onfido_user_kyc?.onfido_doc_data?.last_name}`}
                        </span>
                      ) : user?.fname ? (
                        <span>{`${user?.fname} ${user?.lname ?? ""}`}</span>
                      ) : (
                        <span>N/A</span>
                      )}{" "}
                    </span>
                    <span className="candidate-summary-report-tag">
                      {` ${_t("guarantor")} ${gIdx + 1} of ${_t("tenant")} ${
                        index + 1
                      }`}
                    </span>
                  </div>
                  {(() => {
                    const userData = getIncome(
                      user,
                      tenantUser?.income_selection?.guarantor_sources?.[
                        user?._id
                      ]
                    );
                    if (!Array.isArray(userData?.source)) return null;

                    return (
                      <>
                        <div
                          className={`candidate-summary-report-income-details ${
                            guarantorsWrapped[index] && "order-2 m-0"
                          }`}
                        >
                          <div className="candidate-summary-report-label">
                            {_t("incomeSource")}
                          </div>
                          {userData.source.map((src, idx) => {
                            if (src?.startsWith?.("payslip:")) {
                              const [label, rawDate] = src.split(":");
                              const parts = rawDate.trim().split(" ");

                              let month = parts[0].replace(",", "");
                              const hasComma = parts[0].includes(",");
                              const rest = parts.slice(1).join(" ");

                              return (
                                <span
                                  key={idx}
                                  className="candidate-summary-report-source-tag"
                                >
                                  {_t(label)}: {_t(month)}
                                  {hasComma ? "," : ""}
                                  {rest ? " " + rest : ""}
                                </span>
                              );
                            }

                            return (
                              <span
                                key={idx}
                                className="candidate-summary-report-source-tag"
                              >
                                {_t(src)}
                              </span>
                            );
                          })}
                        </div>

                        <span
                          className={`candidate-summary-report-income-box ${
                            guarantorsWrapped[index] && "order-1"
                          }`}
                        >
                          {_t("income")}: {userData?.amount ?? 0} €
                        </span>
                      </>
                    );
                  })()}
                </div>
              ))
          )}
        </div>
      )}
    </>
  );
};

export default CandidateSummary;
